import Component from '@glimmer/component'
import { guidFor } from '@ember/object/internals'
import { service } from '@ember/service'
import type SubscriptionTypeService from 'teacher-ui-client/services/subscription-type'
import { BaseSelect } from 'teacher-ui-client/components/base/select'
import { themeKey } from 'teacher-ui-client/helpers/theme-key'
import { t } from 'ember-intl'
import { eq, or } from 'ember-truth-helpers'
import { on } from '@ember/modifier'
import { eventValue } from 'teacher-ui-client/helpers/event-value'
import { Precinct } from 'teacher-ui-client/utils/precincts'
import { trackedFunction } from 'reactiveweb/function'
import type LessonInfoService from 'teacher-ui-client/services/lesson-info'
import type { AssignmentOption } from 'teacher-ui-client/components/assignments-admin/assignment-form'
import { assert } from '@ember/debug'

interface Signature {
  Args: {
    /** Only setup for WL_WRITING and FP_PEAKS */
    precinct: Precinct
    selectAssignmentAction: (option?: AssignmentOption) => void
    selectedAssignmentOption: AssignmentOption | undefined
  }
}

export class ActivitySelectorLessonList extends Component<Signature> {
  @service subscriptionType!: SubscriptionTypeService

  @service lessonInfo!: LessonInfoService

  loadOptions = trackedFunction(this, async () => {
    assert(
      'unexpected precinct for lesson-list',
      this.args.precinct === Precinct.WL_WRITING || this.args.precinct === Precinct.FP_PEAKS,
    )

    await this.lessonInfo.loadLessonInfoFor([this.args.precinct])
    return this.lessonInfo.lessonsFor(this.args.precinct).map<AssignmentOption>((data) => ({
      id: String(data.lesson_number),
      label: 'name' in data && 'description' in data ? `${data.name}: ${data.description}` : String(data.lesson_number),
      assignmentData: {
        assignment_id: String(data.lesson_number),
        assignment_type: 'lesson',
        description: 'description' in data ? data.description : undefined,
      },
    }))
  })

  get options() {
    return this.loadOptions.value ?? []
  }

  get componentId() {
    return guidFor(this)
  }

  selectOption = (optionId: string) => {
    const foundOption = this.options.find((option) => option.id === optionId)
    this.args.selectAssignmentAction(foundOption)
  }

  <template>
    <div class="flex justify-between md:flex-wrap">
      <div class="mb-4 px-3 md:w-full lg:w-1/2">
        <label id="{{this.componentId}}-assignment-lessons-label" class="mb-1">
          {{#if (eq @precinct Precinct.FP_PEAKS)}}
            {{t "assignments.create.activityPeak"}}
          {{else}}
            {{t "assignments.create.activityLesson"}}
          {{/if}}
        </label>
        <BaseSelect
          data-test-select-lesson
          class="w-full"
          name="activity-lesson"
          value={{or @selectedAssignmentOption.id ""}}
          aria-labelledby="{{this.componentId}}-assignment-lessons-label"
          @themeKey={{themeKey this.subscriptionType.current}}
          {{on "input" (eventValue this.selectOption)}}
        >
          <option value="">
            {{#if (eq @precinct Precinct.FP_PEAKS)}}
              {{t "assignments.create.activityPeakPlaceholder"}}
            {{else}}
              {{t "assignments.create.activityLessonPlaceholder"}}
            {{/if}}
          </option>
          {{#each this.options as |optionItem|}}
            <option value={{optionItem.id}} selected={{eq optionItem.id @selectedAssignmentOption.id}}>
              {{optionItem.label}}
            </option>
          {{/each}}
        </BaseSelect>
      </div>
    </div>
  </template>
}
